import { GetFeedbackError, useGetFeedback } from "@appretio/feedback";
import { useTenantContext } from "@appretio/shared";
import { Button, ProgressSpinner } from "@appretio/ui-kit";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { resolveAppRedirect } from "../../appRedirects";
import {
  restoreActions,
  restoreCard,
  restoreMessage,
  restorePageBase,
  restoreTitle
} from "./RestoreFeedbackPage.css";

/**
 * The in-app path a stored `page` may navigate to, or `null` when it is not
 * one — so a tampered row can never send an admin off-app.
 *
 * The candidate is resolved against the app's own origin and its `origin` is
 * compared, because a string pattern cannot decide this. The URL parser
 * rewrites backslashes to slashes and strips tab, newline and carriage
 * return before it reads the host, so `/\evil.com` and `/<TAB>/evil.com`
 * both resolve to `https://evil.com` while still looking root-relative as
 * text. The caller navigates to the returned normalised path, never to the
 * raw stored string, so what was checked is what is used.
 *
 * A path back into this route is rejected too: a self-referencing row would
 * loop the restore forever. A redirect-only path (`/`, a legacy URL) is
 * resolved to its final page, so the payload isn't lost to the redirect.
 */
function restorablePath(page: string): string | null {
  const appOrigin = window.location.origin;
  let target: URL;
  try {
    target = new URL(page, appOrigin);
  } catch {
    return null;
  }
  if (target.protocol !== "http:" && target.protocol !== "https:") return null;
  if (target.origin !== appOrigin) return null;
  const pathname = target.pathname.toLowerCase();
  if (
    pathname === "/restore-feedback" ||
    pathname.startsWith("/restore-feedback/")
  ) {
    return null;
  }
  // Resolve redirect-only paths (the app root, legacy URLs) to the page
  // they land on: a redirect hop would drop the restore payload.
  return `${resolveAppRedirect(target.pathname)}${target.search}${target.hash}`;
}

/**
 * Landing route for feedback restore links
 * (`/restore-feedback/:feedbackId?org=<tenant>`, linked from the feedback
 * table and the Teams card). Fetches the trimmed report and forwards the
 * session onto the reported page with the restore payload in router state —
 * the page's own hydration hook (e.g. Tradex's `useTradexStateRestore`)
 * takes it from there. This restores VIEW state, never data: the underlying
 * data is live and has moved on since the report.
 *
 * Tenant handling: per-tenant databases make a foreign tenant's id a plain
 * 404 here. When the link carries an `?org=` hint that differs from the
 * active tenant, the not-found screen says so and offers the switch (when
 * the caller is a member). The AuthGuard consumes the same hint pre-login,
 * so a cold click usually lands in the right tenant already.
 */
export function RestoreFeedbackPage() {
  const { feedbackId } = useParams();
  const [searchParams] = useSearchParams();
  const orgHint = searchParams.get("org");
  const navigate = useNavigate();
  const { currentOrg, organizations, isLoading, isSwitching, switchOrg } =
    useTenantContext();
  const { data, error } = useGetFeedback(feedbackId);
  const [switchFailed, setSwitchFailed] = useState(false);

  const restorePath = data ? restorablePath(data.page) : null;
  const invalidPage = Boolean(data) && restorePath === null;

  useEffect(() => {
    if (!data || !restorePath) return;
    // `page` was captured as pathname+search — replace so Back skips the
    // restore URL instead of re-triggering the whole flow.
    navigate(restorePath, { replace: true, state: { feedbackRestore: data } });
  }, [data, restorePath, navigate]);

  if (invalidPage) {
    return (
      <div className={restorePageBase}>
        <div className={restoreCard}>
          <h1 className={restoreTitle}>Couldn&apos;t restore the report</h1>
          <p className={restoreMessage}>
            This report&apos;s page reference is invalid.
          </p>
          <div className={restoreActions}>
            <Button variant="secondary" onClick={() => navigate("/")}>
              Go to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // The 404 screen needs the (async) org list to decide on the switch.
  if (!error || isLoading) {
    return (
      <div className={restorePageBase}>
        <div className={restoreCard}>
          <ProgressSpinner />
          <p className={restoreMessage}>Restoring the reported view…</p>
        </div>
      </div>
    );
  }

  const status = error instanceof GetFeedbackError ? error.status : null;
  const hintedElsewhere = Boolean(
    orgHint && currentOrg && orgHint !== currentOrg.name
  );
  const hintedOrg = hintedElsewhere
    ? organizations.find((org) => org.name === orgHint)
    : undefined;

  let title = "Couldn't restore the report";
  let message =
    "Something went wrong while loading this feedback report. Try again, or open the page from the feedback table.";
  if (status === 403) {
    title = "No access to feedback reports";
    message =
      "Opening a reporter's view requires an admin or trader-admin role — the report carries the reporter's identity.";
  } else if (status === 404 && hintedElsewhere) {
    title = "This link belongs to another tenant";
    message = hintedOrg
      ? `This feedback link is for the "${orgHint}" tenant, but you are currently in "${currentOrg?.name}". Switch tenants to open it.`
      : `This feedback link is for the "${orgHint}" tenant, but you are currently in "${currentOrg?.name}" and don't have access to "${orgHint}".`;
  } else if (status === 404) {
    title = "Report not found";
    message =
      "This feedback report doesn't exist in your tenant. The link may be stale, or it belongs to another tenant.";
  }

  return (
    <div className={restorePageBase}>
      <div className={restoreCard}>
        <h1 className={restoreTitle}>{title}</h1>
        <p className={restoreMessage}>{message}</p>
        <div className={restoreActions}>
          {hintedOrg && (
            <Button
              variant="primary"
              disabled={isSwitching}
              onClick={() => {
                // Rejects only when the redirect can't launch; on success the
                // page navigates away (and re-enters this route org-scoped).
                setSwitchFailed(false);
                void switchOrg(hintedOrg.id).catch(() => {
                  setSwitchFailed(true);
                });
              }}
            >
              Switch to {hintedOrg.display_name}
            </Button>
          )}
          <Button variant="secondary" onClick={() => navigate("/")}>
            Go to Home
          </Button>
        </div>
        {switchFailed && (
          <p className={restoreMessage} role="alert">
            Couldn&apos;t switch tenants — try again, or switch from the tenant
            menu.
          </p>
        )}
      </div>
    </div>
  );
}
